import type { WebPlayLocalizationService } from '../types'

const WEGLOT_SCRIPT_SRC = 'https://cdn.weglot.com/weglot.min.js'
const DEFAULT_LANGUAGE_SEARCH_PARAMETER = 'lang'

interface WeglotClient {
  initialized: boolean
  initialize(options: { api_key: string; hide_switcher: boolean }): void
  on(event: 'initialized', callback: () => void): void
  on(event: 'languageChanged', callback: (language: string) => void): void
  off(event: 'initialized', callback: () => void): boolean
  off(event: 'languageChanged', callback: (language: string) => void): boolean
  switchTo(language: string): void
  getAvailableLanguages(): string[]
  getCurrentLang(): string
  options: { language_from: string }
}

declare global {
  interface Window {
    Weglot?: WeglotClient
  }
}

let scriptLoadPromise: Promise<WeglotClient> | null = null
let initializationPromise: Promise<WeglotClient> | null = null

function getLoadedWeglot(): WeglotClient | null {
  return window.Weglot ?? null
}

function loadWeglot(): Promise<WeglotClient> {
  const loadedWeglot = getLoadedWeglot()
  if (loadedWeglot) return Promise.resolve(loadedWeglot)
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${WEGLOT_SCRIPT_SRC}"]`)
    const script = existingScript ?? document.createElement('script')

    const handleLoad = () => {
      const weglot = getLoadedWeglot()
      if (weglot) {
        resolve(weglot)
      } else {
        scriptLoadPromise = null
        reject(new Error('Weglot loaded without exposing its browser API'))
      }
    }
    const handleError = () => {
      scriptLoadPromise = null
      reject(new Error('Failed to load the Weglot browser script'))
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    if (!existingScript) {
      script.src = WEGLOT_SCRIPT_SRC
      script.async = true
      document.head.appendChild(script)
    }
  })

  return scriptLoadPromise
}

function resetInvalidDefaultLanguage(weglot: WeglotClient) {
  if (window.self !== window.top) return

  const defaultLanguage = new URL(window.location.href).searchParams.get(DEFAULT_LANGUAGE_SEARCH_PARAMETER)

  if (weglot.getAvailableLanguages().every(language => language !== defaultLanguage)) {
    weglot.switchTo(weglot.options.language_from)
  }
}

function createLocalizationService(weglot: WeglotClient): WebPlayLocalizationService {
  return {
    getAvailableLanguages: () => weglot.getAvailableLanguages(),
    getCurrentLanguage: () => weglot.getCurrentLang(),
    switchTo: language => weglot.switchTo(language),
    subscribe: callback => {
      const handleLanguageChanged = () => callback()
      weglot.on('languageChanged', handleLanguageChanged)
      return () => {
        weglot.off('languageChanged', handleLanguageChanged)
      }
    },
  }
}

/** Load Weglot for web plays and expose it without its generated UI. */
export async function initWeglot(weglotApiKey: string): Promise<WebPlayLocalizationService> {
  if (!weglotApiKey) throw new Error('A Weglot API key is required')

  const weglot = await loadWeglot()

  if (weglot.initialized) {
    resetInvalidDefaultLanguage(weglot)
    return createLocalizationService(weglot)
  }

  if (!initializationPromise) {
    initializationPromise = new Promise<WeglotClient>((resolve, reject) => {
      const handleInitialized = () => {
        weglot.off('initialized', handleInitialized)
        resetInvalidDefaultLanguage(weglot)
        resolve(weglot)
      }

      weglot.on('initialized', handleInitialized)

      try {
        weglot.initialize({
          api_key: weglotApiKey,
          hide_switcher: true,
        })
      } catch (error) {
        weglot.off('initialized', handleInitialized)
        initializationPromise = null
        reject(error)
      }
    })
  }

  return createLocalizationService(await initializationPromise)
}
